import Field, { FieldType, TYPE_ITEMS_FIELD_KEY, TYPE_ITEMS_OBJECT_KEY, TYPE_ITEMS_RELATION_KEY } from "@dto/fields";
import { FieldColumn, Item, ParentKey } from "@dto/records";
import { ObjectRelation } from "@dto/relation";
import { hasOnlyOneValue } from "@utils/model";

// A link chain is a LINK field whose displayed field is itself a LINK field of
// the target object: C.linkB displaying B.linkA (-> A.name). Its column is the
// path C -> B -> A: the cell items carry the displayed A record as key and the
// linked B record as PARENT, while the cell edits the C -> B link of the row's C
// record (which the items do not carry).

function displayedField(field: Field | null | undefined, fieldByKeys: Map<string, Field> | null | undefined): Field | undefined {
    if (field?.type !== FieldType.Link) {
        return undefined;
    }
    return fieldByKeys?.get(field.typeItems?.get(TYPE_ITEMS_FIELD_KEY));
}

export function isLinkChainField(field: Field | null | undefined, fieldByKeys: Map<string, Field> | null | undefined): boolean {
    return displayedField(field, fieldByKeys)?.type === FieldType.Link;
}

// Mirrors the server rule (PathDetail.IsEditableLinkChain): the displayed link
// leads to a plain field and has at most one record per target record (each B
// has at most one A), so a B record is shown by a single value. Other chains are
// read-only.
export function isEditableLinkChain(field: Field | null | undefined, relationByKeys: Map<string, ObjectRelation> | null | undefined, fieldByKeys: Map<string, Field> | null | undefined): boolean {
    const displayedLink = displayedField(field, fieldByKeys);
    if (displayedLink?.type !== FieldType.Link) {
        return false;
    }
    const leafField = displayedField(displayedLink, fieldByKeys);
    if (!leafField || leafField.type === FieldType.Link) {
        return false;
    }
    const relation = relationByKeys?.get(displayedLink.typeItems?.get(TYPE_ITEMS_RELATION_KEY));
    if (!relation) {
        return false;
    }
    return hasOnlyOneValue(relation, field!.typeItems?.get(TYPE_ITEMS_OBJECT_KEY));
}

// A link chain column is edited from the row record: it is read-only when that
// record is ambiguous (a pivot column: each one shows another record of the row)
// or when the chain is not editable (see isEditableLinkChain).
export function isReadOnlyLinkChainColumn(fieldColumn: FieldColumn | null | undefined, relationByKeys: Map<string, ObjectRelation> | null | undefined, fieldByKeys: Map<string, Field> | null | undefined): boolean {
    const field = fieldColumn?.srcField ?? fieldColumn?.field;
    if (!isLinkChainField(field, fieldByKeys)) {
        return false;
    }
    return !!fieldColumn?.isPivot || !isEditableLinkChain(field, relationByKeys, fieldByKeys);
}

// Key of the record of the link field's target object an item points to, or
// undefined when there is none. Works for the grid cells and for the lines of
// the record selector (the target object's records, where the displayed link is
// a direct link whose parent is the target record).
export function linkedRecordKey(item: Item | null | undefined, field: Field, fieldByKeys: Map<string, Field> | null | undefined): string | undefined {
    if (isLinkChainField(field, fieldByKeys)) {
        const targetObjectKey = field.typeItems?.get(TYPE_ITEMS_OBJECT_KEY);
        if (item?.parentKey?.recordKey && item.parentKey.objectKey === targetObjectKey) {
            return item.parentKey.recordKey;
        }
        return undefined;
    }
    return item?.key?.recordKey || undefined;
}

// Record whose link a cell edits. For a chain it is the row's record of the
// current object (rowRecordKey, see getRecordKeys), null when unknown; otherwise
// the parent of the item, as before.
export function linkSourceKey(item: Item | null | undefined, field: Field, fieldByKeys: Map<string, Field> | null | undefined, objectKey: string | undefined, rowRecordKey: string | undefined): ParentKey | null {
    if (isLinkChainField(field, fieldByKeys)) {
        if (!objectKey || !rowRecordKey) {
            return null;
        }
        return { objectKey: objectKey, recordKey: rowRecordKey };
    }
    return item?.parentKey ?? null;
}
